import { useEffect, useState } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { LogIn, Languages, UserPlus, KeyRound, SearchCheck, Mail, Megaphone, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api, errMsg } from "../lib/api";
import { Btn, Field, Input } from "../components/common";
import { SecretInput } from "../components/PasswordFields";

const google = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function Notices({ front, t }) {
  if (!front) return null;
  return (
    <div className="space-y-3" data-testid="front-notices">
      {front.maintenance_notice && <p className="rounded-xl bg-[#FEF3C7] p-3 text-sm font-semibold text-[#92400E]" data-testid="maintenance-notice">{front.maintenance_notice}</p>}
      {front.front_announcements && <div className="rounded-xl bg-white border border-[#E4DACD] p-4" data-testid="announcements"><p className="font-bold flex items-center gap-2 mb-1"><Megaphone className="h-4 w-4 text-[#C85A32]" />{t("announcements")}</p><p className="text-sm whitespace-pre-line">{front.front_announcements}</p></div>}
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const { t, toggle, nextLabel } = useI18n();
  const loc = useLocation();
  const [f, setF] = useState({ email: localStorage.getItem("fmis_email") || "", password: "" });
  const [remember, setRemember] = useState(!!localStorage.getItem("fmis_email"));
  const [err, setErr] = useState(loc.state?.err || "");
  const [busy, setBusy] = useState(false);
  const [front, setFront] = useState(null);
  useEffect(() => { api.get("/public/front").then((r) => setFront(r.data)).catch(() => {}); }, []);
  if (user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try {
      if (remember) localStorage.setItem("fmis_email", f.email.trim()); else localStorage.removeItem("fmis_email");
      await login(f.email.trim(), f.password);
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const contact = front?.contact_info || "Parish Office";
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF7F2]">
      <div className="relative hidden lg:block">
        <img src="https://images.unsplash.com/photo-1642636495318-6ea8f95b69cb?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400" alt="Parish altar" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#2F2A24]/75" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <p className="text-[#D4AF37] font-bold tracking-[0.25em] text-sm">TRANSPARENCY • ACCOUNTABILITY • INTEGRITY • STEWARDSHIP</p>
          <p className="font-serif text-2xl leading-snug max-w-lg mt-4">{t("welcome")}</p>
        </div>
      </div>
      <div className="flex flex-col justify-center px-5 py-8 sm:px-12">
        <div className="max-w-md w-full mx-auto fade-up space-y-6">
          <div className="flex items-center justify-between">
            <img src="/parish-logo.png" alt="Saint Francis of Assisi Parish seal" className="h-16 w-16 rounded-full object-cover" data-testid="parish-logo" />
            <button data-testid="login-language-toggle" onClick={toggle} className="h-11 rounded-full bg-[#D4AF37] px-4 font-bold text-sm inline-flex items-center gap-1.5"><Languages className="h-4 w-4" />{nextLabel}</button>
          </div>
          <div>
            <p className="text-base font-black uppercase tracking-[0.15em] text-[#5C4033]" data-testid="parish-name">Saint Francis of Assisi Parish</p>
            <p className="text-[#6E6259]">Barangay Mabuhay, General Santos City</p>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#2F2A24] mt-2">GKK FINANCIAL MANAGEMENT INFORMATION SYSTEM (GKK-FMIS)</h1>
            <p className="text-xs font-bold tracking-[0.2em] text-[#C85A32] mt-2 lg:hidden">TRANSPARENCY • ACCOUNTABILITY • INTEGRITY • STEWARDSHIP</p>
            <p className="text-sm text-[#4B4038] mt-3 lg:hidden">{t("welcome")}</p>
          </div>
          <Notices front={front} t={t} />
          <Btn v="outline" className="w-full h-14 text-base" onClick={google} data-testid="google-signin-button">
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" /><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z" /><path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8z" /><path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" /></svg>
            {t("google_btn")}
          </Btn>
          <div className="flex items-center gap-3 text-sm text-[#6E6259]"><span className="h-px flex-1 bg-[#E4DACD]" />{t("email_login")}<span className="h-px flex-1 bg-[#E4DACD]" /></div>
          <form onSubmit={submit} className="space-y-4" data-testid="email-login-form">
            <Field label={t("email")}><Input data-testid="login-username-input" type="text" inputMode="email" autoCapitalize="none" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
            <SecretInput label={t("password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="login-password-input" autoComplete="current-password" />
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" className="h-5 w-5" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-email-checkbox" />{t("remember_email")}</label>
              <Link to="/forgot-password" className="font-semibold text-[#C85A32]" data-testid="forgot-password-link">{t("forgot")}</Link>
            </div>
            {err && <p data-testid="login-error" className="rounded-xl bg-[#FEF2F2] text-[#B91C1C] p-3 font-semibold">{err}</p>}
            <Btn data-testid="login-submit-button" type="submit" loading={busy} disabled={busy} className="w-full h-14 text-base"><LogIn className="h-5 w-5" /> {t("login")}</Btn>
          </form>
          <div className="grid gap-3">
            <Link to="/register" data-testid="register-link"><Btn v="terra" className="w-full h-14 text-base"><UserPlus className="h-5 w-5" /> {t("register_btn")}</Btn></Link>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/registration-status" data-testid="status-link"><Btn v="ghost" className="w-full"><SearchCheck className="h-5 w-5" /> {t("check_status")}</Btn></Link>
              <a href="#contact" data-testid="contact-link"><Btn v="ghost" className="w-full"><Mail className="h-5 w-5" /> {t("contact_admin")}</Btn></a>
            </div>
          </div>
          <p className="rounded-xl bg-[#F3EDE2] p-3 text-sm flex gap-2" data-testid="officer-notice"><ShieldCheck className="h-5 w-5 shrink-0 text-[#5C4033]" />{t("notice_only")}</p>
          <div id="contact" className="text-sm text-[#4B4038]" data-testid="contact-info"><p className="font-bold">{t("contact_admin")}</p><p className="whitespace-pre-line">{contact}</p></div>
          <p className="text-xs text-[#6E6259] flex items-center gap-1"><KeyRound className="h-3 w-3" /> GKK-FMIS never asks for your Google password. <Link to="/public" data-testid="login-public-link" className="font-semibold text-[#C85A32]">{t("public_page")} →</Link></p>
        </div>
      </div>
    </div>
  );
}
